import React, { useCallback, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router'
import { $TOWNS } from '@components/$TOWNS/$TOWNS'
import { useStakingDeposits } from '@components/ClaimRewardsPanel/hooks/useStakingData'
import { NodeOperatorCard } from '@components/DelegateToNodeOperatorPanel/operators/NodeOperatorCard'
import { Panel } from '@components/Panel/Panel'
import { useHasMemberNftInAbstractAccount } from '@components/StakeToTownPanel/hooks/useHasMemberNftInAbstractAccount'
import { useRedelegateStake } from '@components/StakeToTownPanel/hooks/useRedelegateStake'
import { StakeSpaceCard } from '@components/StakeToTownPanel/StakeSpaceCard'
import {
    NodeOperatorDropdown,
    SpaceDropdown,
    WarnAboutMembershipNft,
} from '@components/StakeToTownPanel/StakeToTownPanel'
import { Box, FancyButton, Paragraph, Stack } from '@ui'
import { useMyAbstractAccountAddress } from 'hooks/useAbstractAccountAddress'
import { StakableOperator, useFetchOperatorsQuery } from 'hooks/useOperatorData'
import { GetSigner, WalletReady } from 'privy/WalletReady'
import { CHANNEL_INFO_PARAMS } from 'routes'
import { usePanelActions } from 'routes/layouts/hooks/usePanelActions'
import { atoms } from 'ui/styles/atoms.css'
import {
    NULL_ADDRESS,
    SpaceItem,
    useSpaceDelegationOperator,
    useStakingDepositDetailsQueries,
    useTownsContext,
} from 'use-towns-client'
import { getAddress } from 'viem'

export const RedelegateStakePanel = React.memo(() => {
    const [renderKey, setRenderKey] = useState(0)

    return <RedelegateStakePanelInternal key={renderKey} setRenderKey={setRenderKey} />
})

export const RedelegateStakePanelInternal = ({
    setRenderKey,
}: {
    setRenderKey: React.Dispatch<React.SetStateAction<number>>
}) => {
    const [searchParams] = useSearchParams()
    const depositId = searchParams.get('depositId')
        ? BigInt(searchParams.get('depositId')!)
        : undefined
    const { spaces } = useTownsContext()

    const depositsData = useStakingDepositDetailsQueries({
        depositIds: depositId ? [depositId] : [],
        enabled: !!depositId,
    }).at(0)?.data

    const fromSpace = spaces.find(
        (o) => o.address.toLowerCase() === depositsData?.delegatee.toLowerCase(),
    )

    const fromSpaceAddress = fromSpace?.address
        ? (getAddress(fromSpace.address) as `0x${string}`)
        : undefined

    const isPendingWithdrawal =
        !!depositsData && !fromSpaceAddress && depositsData.pendingWithdrawal.gt(0)

    if (!depositId) {
        return (
            <Panel label={isPendingWithdrawal ? 'Cancel and Restake' : 'Change Town'}>
                <Box>
                    <Paragraph>
                        This deposit is not associated with an active town. Please select a
                        different deposit.
                    </Paragraph>
                </Box>
            </Panel>
        )
    }

    return (
        <Panel label={isPendingWithdrawal ? 'Cancel and Restake' : 'Change Town'}>
            <PanelContent
                spaces={spaces}
                fromSpaceAddress={fromSpaceAddress}
                depositId={depositId}
                isPendingWithdrawal={isPendingWithdrawal}
                pendingWithdrawal={depositsData?.pendingWithdrawal.toBigInt()}
                setRenderKey={setRenderKey}
            />
        </Panel>
    )
}

type PanelContentProps = {
    spaces: SpaceItem[]
    fromSpaceAddress: `0x${string}` | undefined
    depositId: bigint
    isPendingWithdrawal: boolean
    pendingWithdrawal: bigint | undefined
    setRenderKey: React.Dispatch<React.SetStateAction<number>>
}

const PanelContent = ({
    spaces,
    fromSpaceAddress,
    depositId,
    isPendingWithdrawal,
    pendingWithdrawal,
    setRenderKey,
}: PanelContentProps) => {
    const { data: owner } = useMyAbstractAccountAddress()
    const {
        data: { groupedDeposits },
    } = useStakingDeposits({ owner })
    const [delegatee, setDelegatee] = useState<`0x${string}` | undefined>(undefined)
    const [operatorAddress, setOperatorAddress] = useState<`0x${string}` | undefined>(undefined)
    const { submitRedelegate } = useRedelegateStake()

    const { data: currentOperator } = useSpaceDelegationOperator(delegatee)

    const { data: operators } = useFetchOperatorsQuery()

    const needsOperatorSelection = useMemo(() => {
        if (!delegatee || !currentOperator) {
            return false
        }
        return !currentOperator || currentOperator === NULL_ADDRESS
    }, [delegatee, currentOperator])

    const selectedOperator = useMemo(() => {
        if (!operatorAddress || !operators?.operators) {
            return null
        }
        return (
            operators.operators.find((op: StakableOperator) => op.address === operatorAddress) ||
            null
        )
    }, [operatorAddress, operators?.operators])

    const { combinedDepositIds, combinedAmount, targetStake, spacesExcludingCurrent } =
        useMemo(() => {
            const combinedDepositIds = isPendingWithdrawal
                ? [depositId]
                : (groupedDeposits
                      .find((o) => o.space === fromSpaceAddress)
                      ?.deposits.map((o) => o.depositId) ?? [])

            const combinedAmount = isPendingWithdrawal
                ? (pendingWithdrawal ?? 0n)
                : (groupedDeposits
                      .find((o) => o.space === fromSpaceAddress)
                      ?.totalStaked.toBigInt() ?? 0n)

            const targetStake = groupedDeposits.find((o) => o.space === delegatee)
            const spacesExcludingCurrent = spaces.filter((o) => o.address !== fromSpaceAddress)

            return { combinedDepositIds, combinedAmount, targetStake, spacesExcludingCurrent }
        }, [
            isPendingWithdrawal,
            depositId,
            groupedDeposits,
            pendingWithdrawal,
            spaces,
            fromSpaceAddress,
            delegatee,
        ])

    const { openPanel } = usePanelActions()
    const {
        data: hasMemberNftInAbstractAccount,
        isLoading: isLoadingHasMemberNftInAbstractAccount,
    } = useHasMemberNftInAbstractAccount({ spaceId: delegatee })

    const showMembershipNftWarning = useMemo(() => {
        if (isLoadingHasMemberNftInAbstractAccount) {
            return false
        }
        return !hasMemberNftInAbstractAccount?.isMember
    }, [isLoadingHasMemberNftInAbstractAccount, hasMemberNftInAbstractAccount?.isMember])

    const onUpdateStake = useCallback(
        async ({ getSigner }: { getSigner: GetSigner }) => {
            if (delegatee && combinedDepositIds.length > 0) {
                try {
                    console.log('[redelegate-to-town] Redelegating with operator:', operatorAddress)
                    await submitRedelegate({
                        depositIds: combinedDepositIds,
                        delegatee,
                        operatorAddress: needsOperatorSelection ? operatorAddress : undefined,
                        getSigner,
                        onAlreadyDelegated: () => {
                            setRenderKey((prev) => prev + 1)
                        },
                    })

                    // Navigate back to trading wallet with restaked deposit info
                    const restakeInfo = {
                        spaceId: delegatee,
                        amount: combinedAmount.toString(),
                        isNewDeposit: false,
                        timestamp: Date.now(),
                    }

                    openPanel(CHANNEL_INFO_PARAMS.TRADING_WALLET, {
                        data: JSON.stringify({
                            activeTab: 'staked',
                            newDeposit: restakeInfo,
                        }),
                    })

                    setDelegatee(undefined)
                    setOperatorAddress(undefined)
                } catch (e) {
                    console.error(e)
                }
            }
        },
        [
            delegatee,
            combinedDepositIds,
            operatorAddress,
            submitRedelegate,
            needsOperatorSelection,
            combinedAmount,
            openPanel,
            setRenderKey,
        ],
    )

    return (
        <Stack grow gap="lg">
            {!!fromSpaceAddress && (
                <Stack gap="paragraph">
                    {!isPendingWithdrawal && (
                        <Paragraph fontWeight="strong">Current Stake</Paragraph>
                    )}
                    <StakeSpaceCard
                        spaceAddress={fromSpaceAddress!}
                        spaces={spaces}
                        amount={combinedAmount}
                    />
                </Stack>
            )}
            <Stack gap="paragraph">
                <Paragraph fontWeight="strong">Restake to</Paragraph>
                <SpaceDropdown
                    spaces={spacesExcludingCurrent}
                    selected={delegatee as `0x${string}`}
                    error={undefined}
                    onChange={(spaceAddress) =>
                        setDelegatee(spaceAddress as `0x${string}` | undefined)
                    }
                />
                {delegatee && (
                    <StakeSpaceCard
                        spaceAddress={delegatee}
                        spaces={spacesExcludingCurrent}
                        amount={targetStake?.deposits.reduce(
                            (acc, deposit) => acc + deposit.amount.toBigInt(),
                            0n,
                        )}
                    />
                )}

                {needsOperatorSelection && delegatee && !showMembershipNftWarning && (
                    <Stack gap="paragraph">
                        <Paragraph fontWeight="strong">Select Operator</Paragraph>
                        <NodeOperatorDropdown
                            selected={operatorAddress}
                            error={undefined}
                            operators={operators?.operators || []}
                            onChange={(address) => setOperatorAddress(address as `0x${string}`)}
                        />
                        {operatorAddress && selectedOperator && (
                            <NodeOperatorCard
                                operator={selectedOperator}
                                operatorAddress={operatorAddress}
                            />
                        )}
                    </Stack>
                )}
                {needsOperatorSelection && showMembershipNftWarning && <WarnAboutMembershipNft />}
                {combinedAmount && (
                    <Box gap="paragraph">
                        <Paragraph color="default" fontWeight="strong">
                            Amount
                        </Paragraph>
                        <Paragraph>
                            <$TOWNS value={combinedAmount} />
                            {combinedDepositIds.length > 1 && (
                                <span className={atoms({ color: 'gray2' })}>
                                    {' '}
                                    {combinedDepositIds.length} deposits
                                </span>
                            )}
                        </Paragraph>
                    </Box>
                )}
            </Stack>
            <Stack justifyContent="end">
                <WalletReady>
                    {({ getSigner }) => (
                        <FancyButton
                            cta={!!delegatee && (!needsOperatorSelection || !!operatorAddress)}
                            disabled={!delegatee || (needsOperatorSelection && !operatorAddress)}
                            borderRadius="full"
                            onClick={() => onUpdateStake({ getSigner })}
                        >
                            {isPendingWithdrawal ? 'Restake' : 'Update Stake'}
                        </FancyButton>
                    )}
                </WalletReady>
            </Stack>
        </Stack>
    )
}
